/* tslint:disable */
/* eslint-disable */
/**
 * REAP API
 * Public REST API for REAP (multi-tenant, agentic AI for network operations).  ### Identifier conventions - **id**: UUID — canonical and used for foreign keys and writes. - **slug**: immutable, human-readable handle (kebab-case) used in configs/URLs. Unique per scope (org) or globally (orgs). - **name**: human-friendly display label (mutable). Do **not** use as a reference key.  ## Authentication Overview  This API supports local credentials (password, passkeys), SSO (OIDC/SAML), and email-verified bootstraps for registration/reset/invitations. The design is enumeration-resistant and minimizes clicks for the common case (daily sign-in).  ### Daily sign-ins (no emailTx required) - **Password**: `POST /auth/password/verify` with `{ email, password }` → returns **loginTx**. - **Passkey (email-first)**: `POST /auth/passkeys/assert` with `{ email }` → WebAuthn options + `challengeToken`   → `POST /auth/passkeys/assert/verify` → **loginTx**. - **Usernameless passkey** (optional): `POST /auth/passkeys/assert` with `{ usernameless: true }`.  Exchange **loginTx** at `POST /auth/token`: - If already org-bound (SSO, single org, or server inference) → returns **TokenResponse**. - If not org-bound and multiple orgs exist → returns **200** with `{ nextStep: \"pickOrg\", memberships:[...] }`.   The server **SHOULD filter out ineligible orgs** so the client can re-call `/auth/token` with `orgId`   without additional policy checks. - To **switch orgs** later, call `POST /auth/token` with `grantType:\"refresh\"` and `orgId`.  ### SSO (OIDC/SAML) - IdP flows produce **`oidcTx`** or **`samlTx`**, which you exchange at `POST /auth/token`. - HRD (home realm discovery): `POST /auth/discover/start` always replies **200** with a discriminated   `nextStep`:     - `sso_redirect` (single, safe target) — redirect using `connection.startUrl` (optionally include `login_hint`).     - `picker` (multiple targets) — render an org/connection picker.     - `verify_email` — prove mailbox control before revealing org/connection detail.   *No membership/policy status leaks at Start; the rich, email-proven view is returned by `/auth/discover/verify`.*  Email verification is a common bootstrap across Discovery and Invitations. Invitation Accept returns emailTx plus invited-org context (policy and SSO connections) without enumerating other orgs. Clients should drive UX from these fields and exchange loginTx/oidcTx/samlTx at `/auth/token`.  ### Email-verified bootstraps (keep `emailTx`) - **Register password/passkey**, **reset password**, and **invitation acceptance** require proving email ownership.   After `/auth/discover/verify` (or `/auth/invitations/accept`) you get **`emailTx`** for these operations or to   obtain a limited pre-org session at `/auth/token` (for example, to create the first org).  ### Enumeration resistance & security - Return generic errors for failed sign-in; normalize response timing and apply rate-limits with small jitter. - Avoid revealing user/org status or policy in pre-verification responses. - Use HTTPS; set appropriate cache controls; consider device-bound refresh tokens and short-lived access tokens.  ### Client authentication REAP supports two client auth mechanisms:  - **Bearer JWT** (`Authorization: Bearer <jwt>`): short-lived, user/session tokens returned by `/auth/token`.   Backends validate these locally via signature/claims.  - **API Key** (`X-Api-Key: <secret>`): long-lived, org-scoped secrets intended for automation (CI/CD, headless jobs).   Keys are validated by REAP’s auth layer; they are not JWTs and should not be sent as Bearer tokens.  **Guidance** - Prefer **Bearer JWTs** for interactive UIs and short-lived clients. - Use **API keys** for machine-to-machine integrations; rotate or **PATCH** `expiresAt` to extend, and **DELETE** (revoke) when done. - Assign permissions via **roles**: provide `roleIds` on create/patch; responses include `rolesResolved` for admin UX. - Do not place API keys in URLs or logs. Keys are shown **once** on creation.  ### API key return semantics - The full API key **secret is returned only in the 201 Create response** of `/orgs/{orgId}/api-keys`. - It is **never** returned by GET/PATCH/DELETE. - Clients should display it once and prompt the operator to store it securely (password manager, vault). - For subsequent requests, present the key as `X-Api-Key: <secret>`; do **not** use the Bearer scheme for API keys.  ## Auth policies (overview) - **SSO JIT (just-in-time) provisioning** can be enabled per org: SSO users can sign in without invitations. - **Invite-only SSO** (optional): require an invitation before SSO users can join the org. - **Local accounts** (optional): allow bootstrap/break-glass users without SSO. - **Discover** avoids user enumeration; org memberships and sign-in options are revealed only after email verification. - **Invitations** never expose tokens/links via API; the backend emails the invite link to the recipient. - **Org auth policy** is a **singleton** at `/orgs/{orgId}/auth/policy` that controls JIT, invite-only SSO, and local-account allowances.   The policy is retrieved/updated via **GET/PUT** and versioned with **ETag**.  ## Discovery result (structure) - **Local methods are global**: `DiscoverVerifyResponse.localConnections` lists user-level methods (`passkey`, `password`) available for the verified email — shown **once** in the UI. - **SSO is per-org**: `memberships[].connections` lists **org-scoped** SSO options (`oidc`, `saml`). This array may be **empty** if the org has no SSO. - **Policy hint**: `memberships[].policy.requiresSso` indicates that the org **requires** SSO (local login should not grant access there), even if local methods exist globally.  ## Passwordless authentication (overview) - New-user and invitation flows converge on **email verification** → short-lived **`emailTx`** (no user enumeration). - Clients then use **Passkeys** (WebAuthn/FIDO2) to enroll a credential (attestation) or sign in (assertion). - Successful passkey assertion yields **`loginTx`**, which is exchanged at **`POST /auth/token`** with `grantType=login_tx`. - **Token grants** supported at `/auth/token`:     - **authorization_code** (OIDC) via **`oidcTx`**     - **saml2_bearer** (SAML) via **`ssoTx`**     - **login_tx** (Passkey) via **`loginTx`**     - **refresh_token** (rotation; optional `orgId` switch)     - **email_tx** (bootstrap pre-org; limited to safe endpoints like creating the first org) - **Stateless challenge** option for passkeys: options responses include a signed **`challengeToken`** that the client must echo on verify,   allowing the server to validate the challenge/guardrails **without storing per-challenge state** (short TTL recommended). - **Optional fast-path**: `POST /auth/passkeys/attest/verify?issueLoginTx=true` may return `loginTx` immediately after registration   (policy permitting), otherwise the default flow is attest → assert → token. - **Usernameless (passkey-first) sign-in**: the portal may skip email/Discover and call   `POST /auth/passkeys/assert` with `{ \"usernameless\": true }`. The server returns   `PublicKeyCredentialRequestOptions` with an empty/omitted `allowCredentials`. The browser’s   Conditional UI prompts if a matching passkey exists; on success, the client verifies at   `/auth/passkeys/assert/verify` to obtain `loginTx`, then exchanges at `/auth/token`.  ## API response conventions - **No envelopes**: endpoints return typed bodies directly. - **Resources**: single GET/POST returns the resource object (composed from `ResourceBase` and `OrgScoped` when applicable). - **Operational endpoints** (e.g., auth/token, passkeys) return typed operation results (no `ResourceBase`). - **Lists**: return `{ items: [...], meta: { ...} }`, where each item is a full resource object and `meta` follows **PageMeta**. Responses MAY include `Link` headers (`rel=\"next\"`, `rel=\"prev\"`). When `meta.exactTotal` is omitted clients SHOULD treat totals as unknown and render “more available” messaging rather than fabricating counts.  ### Cache-Control conventions - **Mutable resource detail** (single GET, POST, PATCH) and every response that carries credentials, tokens, or bootstrap data sends `Cache-Control: no-store`. Browsers MUST revalidate before rendering drawers/forms so operators see their latest edits. - **Collection/list endpoints** that back dashboards or tables may use `Cache-Control: private, max-age=15` together with `ETag` and `Link` headers. Clients SHOULD revalidate on manual refresh (If-None-Match) and treat the TTL as an upper bound. - **Streaming/event feeds** and download endpoints for bootstrap bundles always respond with `Cache-Control: no-store`. - If a response omits `Cache-Control`, treat it as non-cacheable.  ### Identity & scoping - Every object is keyed by `id` (UUID). - Common audit fields come from `ResourceBase` (`createdAt`, `updatedAt`, `createdBy`, `updatedBy`). - Multi-tenant scoping builds on `OrgScoped` (adds `orgId`); request bodies never include `orgId`—it comes from the path.  ### Pagination & sorting - Pagination via `limit` (1–200, default 50), `after`, `before`. Servers SHOULD return `Link` headers that mirror `meta.next`/`meta.prev` and include `meta.exactTotal` when a precise count is available. - `meta.exactTotal` is OPTIONAL. When omitted it signals “total unknown”; when the repository already computes a count (e.g., `COUNT(*)`) servers SHOULD populate it. - Sort (when supported) is a comma-separated list; prefix with `-` for descending (for example `-lastSeenAt,mgmtAddress`).  ### Concurrency & caching - Mutations return **ETag**; `PATCH`/`DELETE` require **If-Match** (mismatch → **412**). - `GET` supports **If-None-Match** and may return **304 Not Modified**. - Each response includes **`X-Request-Id`** for troubleshooting.  ### Conditional GETs (ETag / If-None-Match) - Servers return **ETag** headers on GETs for cache-friendly revalidation. - Clients can send **If-None-Match** with the last seen ETag:   - If representation is unchanged → **304 Not Modified** (no body).   - If changed → **200 OK** with a new ETag and the latest representation. - Recommended for frequently-polled resources (e.g., devices list, discovery runs, topology graphs).  ### Error model - Structured errors use the `Problem` schema. - Common statuses: **400** (validation), **401/403** (auth/authz), **404** (not found), **409** (conflict), **412** (ETag mismatch), **429** (rate limit).  ### Credentials model (overview) - **Secret vs Credential vs Assignment**   - **Secret**: write-only value + metadata (`type`, `fingerprint`, `last4`). Value is never returned.   - **Credential**: one access **kind** (`cli_ssh`, `snmp_v2c`, `snmp_v3`, `gnmi`, `api_rest`) with kind-specific fields that **reference Secret IDs** (e.g., SSH key, SNMP passwords, API key, mTLS certs).   - **Credential Assignment**: routing rule that applies a **single credential** by scope (org / sites / specific devices) with optional match filters (hostname regex/prefix/suffix, labels, vendor/model).   - Rotate a secret’s value via `POST /value` (use **If-Match** to guard against concurrent rotation). - **Validation**   - On create/update, the server validates that each referenced Secret ID is of the **expected SecretType** for the credential kind (mismatch → 400). - **Resolution order (when multiple assignments match a device)**   1. Lower `priority` first (e.g., 10 wins over 100)   2. Narrower `scope` (device > sites > org)   3. Most recent `updatedAt` - **Consistency**   - Single-resource endpoints return the resource (no envelopes).   - List endpoints return `{ items, meta }` where `meta` follows `PageMeta` and cursors are passed via `limit/after/before`. - **Concurrency & cache**   - Mutations return **ETag**; `PATCH`/`DELETE` require **If-Match**.   - `GET` supports **If-None-Match** to return **304 Not Modified** when ETag matches.  ## Devices (key behaviors)  - **States** — `discovered`, `validated`, `unreachable`, `retired`, `merged` (see `DeviceState`). - **Discovery-managed fields** (for example `vendor`, `model`, `osVersion`, `serial`, `hostname`, `firstSeenAt`, `lastSeenAt`) are **read-only** via API. - **User-editable** fields: `displayName`, `labels` (case-insensitive keys; PATCH overwrites the map), `role` (free text, e.g. `access`, `core`, `firewall`, `wireless-ap`), `notes`. - **Create** is allowed (beta/testing). Default list sort: `-lastSeenAt`. - **Retire** via `PATCH` (`state=retired`). **DELETE** is hard-delete (beta convenience). - **Merge** consolidates duplicates; merged-away device IDs subsequently return **404**.  ## Edge connectivity (Connectors & Connector Clusters)  **Connectors** are lightweight agents (AMI/OVA/etc.) that establish outbound, persistent control channels from customer sites to REAP. Connectors register to an **Organization**, can optionally belong to a **Connector Cluster**, and periodically report health, version, and capabilities.  **Connector Clusters** group one or more connectors for redundancy or capacity. Clusters can be attached to **Sites** (N↔M). The API allows make-before-break migrations: a Site can temporarily be served by multiple clusters. Backend scheduling ensures only one active worker set manages a Site at a time. - The attach result includes **multiAttached** to surface Sites now attached to this cluster and at least one other cluster.  **User-data (first-boot)**: each Connector exposes a one-time, short-lived bootstrap bundle for cloud-init. Content negotiation: - `Accept: text/cloud-config` → cloud-init YAML (default) - `Accept: application/json` → JSON wrapper (opaque to clients) - `Accept: application/base64+json` → base64 of the opaque JSON  One-time semantics: redeemed → **409 Conflict**, expired → **410 Gone**. Responses SHOULD include `Cache-Control: no-store`.  **Concurrency & caching**: mutable single-resource endpoints return **ETag** and require **If-Match** on updates/deletes. Reads MAY accept **If-None-Match** and return **304**. Lists use pagination via `{ items, meta }` (see `PageMeta`).  # SSO / IdP Connections REAP supports per-organization **IdP connections** for **OIDC** and **SAML**. - Connections are modeled as a discriminated union (`type: \"oidc\" | \"saml\"`). - Connection **config** holds IdP metadata. Some fields are **readOnly** and computed by REAP (e.g., `redirectUris` for OIDC; `acsUrl`, `spEntityId` for SAML). - **Concurrency:** single-resource GET returns **ETag**; **PATCH**_/_**DELETE** require **If-Match**. - **Deletes** return **204 No Content**. - **SAML IdP-initiated** sign-in can be allowed/blocked by `IdpSamlConfig.allowIdpInitiated` (default **true**). # OIDC client authentication - Confidential clients only: REAP supports client_secret_basic (default) and client_secret_post. - private_key_jwt and public clients are not supported in this version. - PKCE is supported but off by default; enable per connection via `config.enforcePkce`.  ## Local password authentication (optional) - Fits the same **email-first, enumeration-resistant** pattern: verify email → short-lived **`emailTx`**. - Supports:   - **Register** a password after email verification   - **Verify** password to obtain **`loginTx`** (exchange at `/auth/token` with `grantType=loginTx`)   - **Reset** password (email-verified), **Change** password (bearer session) - Client sends the **clear-text password over HTTPS**; the server performs KDF hashing (e.g., **Argon2id** with per-user salt; optional application-level pepper in KMS). - Store the **password hash (and parameters)** in the **regular user table** (not a secrets manager). If you use a **pepper**, store that pepper as a secret. - Error hardening:   - Same **lockout** semantics as passkeys (use **423** with a neutral error body)   - Consistent, non-enumerating responses for all email-bound flows
 *
 * The version of the OpenAPI document: 1.3.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as runtime from '../runtime';
import { CMDBCanonicalRelationMappingRulesRequestToJSON, CMDBEntityAliasFromJSON, CMDBEntityAliasCreateRequestToJSON, CMDBEntityAliasSplitRequestToJSON, CMDBEntityAliasesResponseFromJSON, CMDBIdentityAdjudicationRequestToJSON, CMDBIdentityAdjudicationResultFromJSON, CMDBIdentityFindingsResponseFromJSON, CMDBIdentityHistoryFromJSON, CMDBIdentityPolicyFromJSON, CMDBIdentityPolicyUpdateRequestToJSON, CMDBPublicationExecutionResultFromJSON, CMDBRelationMappingProfileFromJSON, CMDBSourceRecordHistoryFromJSON, CMDBSourceRecordsResponseFromJSON, CMDBSourceRetentionPolicyFromJSON, CMDBSourceRetentionPolicyUpdateRequestToJSON, IntegrationConnectionFromJSON, IntegrationConnectionCMDBAccessCheckRequestToJSON, IntegrationConnectionCMDBAccessCheckResponseFromJSON, IntegrationConnectionCMDBPublicationRequestToJSON, IntegrationConnectionCMDBSourceEvidenceSummaryFromJSON, IntegrationConnectionCMDBSyncJobFromJSON, IntegrationConnectionCMDBSyncRequestToJSON, IntegrationConnectionCMDBWritebackPolicyFromJSON, IntegrationConnectionCMDBWritebackPolicyUpdateRequestToJSON, IntegrationConnectionCMDBWritebackResponseFromJSON, IntegrationConnectionCreateToJSON, IntegrationConnectionTestResultFromJSON, IntegrationConnectionUpdateToJSON, IntegrationConnectionsListResponseFromJSON, IntegrationSetupRequestFromJSON, IntegrationWebhookProvisioningFromJSON, InteractionRoutingRuleFromJSON, InteractionRoutingRuleCreateRequestToJSON, InteractionRoutingRulesListResponseFromJSON, InteractionTargetFromJSON, InteractionTargetCreateRequestToJSON, InteractionTargetsListResponseFromJSON, OAuthStartRequestToJSON, OAuthStartResponseFromJSON, OrgsOrgIdIntegrationsSetupProviderActionsPostRequestToJSON, ProviderDefinitionsListResponseFromJSON, SlackLinkCompleteRequestToJSON, SlackLinkCompleteResponseFromJSON, } from '../models/index';
/**
 *
 */
export class IntegrationsApi extends runtime.BaseAPI {
    /**
     * Atomically activate a reviewed mapping profile
     * @deprecated
     */
    activateIntegrationConnectionCMDBRelationMappingProfileRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling activateIntegrationConnectionCMDBRelationMappingProfile().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling activateIntegrationConnectionCMDBRelationMappingProfile().');
            }
            if (requestParameters['profileId'] == null) {
                throw new runtime.RequiredError('profileId', 'Required parameter "profileId" was null or undefined when calling activateIntegrationConnectionCMDBRelationMappingProfile().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/connections/{connectionId}/cmdb/relation-mapping-profiles/{profileId}/activate`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))).replace(`{${"profileId"}}`, encodeURIComponent(String(requestParameters['profileId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.VoidApiResponse(response);
        });
    }
    /**
     * Atomically activate a reviewed mapping profile
     * @deprecated
     */
    activateIntegrationConnectionCMDBRelationMappingProfile(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.activateIntegrationConnectionCMDBRelationMappingProfileRaw(requestParameters, initOverrides);
        });
    }
    /**
     * Apply an authorized identity finding decision
     */
    adjudicateIntegrationConnectionCMDBIdentityFindingRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling adjudicateIntegrationConnectionCMDBIdentityFinding().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling adjudicateIntegrationConnectionCMDBIdentityFinding().');
            }
            if (requestParameters['findingId'] == null) {
                throw new runtime.RequiredError('findingId', 'Required parameter "findingId" was null or undefined when calling adjudicateIntegrationConnectionCMDBIdentityFinding().');
            }
            if (requestParameters['cMDBIdentityAdjudicationRequest'] == null) {
                throw new runtime.RequiredError('cMDBIdentityAdjudicationRequest', 'Required parameter "cMDBIdentityAdjudicationRequest" was null or undefined when calling adjudicateIntegrationConnectionCMDBIdentityFinding().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/connections/{connectionId}/cmdb/identity-findings/{findingId}/adjudicate`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))).replace(`{${"findingId"}}`, encodeURIComponent(String(requestParameters['findingId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
                body: CMDBIdentityAdjudicationRequestToJSON(requestParameters['cMDBIdentityAdjudicationRequest']),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CMDBIdentityAdjudicationResultFromJSON(jsonValue));
        });
    }
    /**
     * Apply an authorized identity finding decision
     */
    adjudicateIntegrationConnectionCMDBIdentityFinding(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.adjudicateIntegrationConnectionCMDBIdentityFindingRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Validates CMDB-related read access required for sync and, when policy-enabled, writeback-related table access required for CI create/update workflows.
     * Validate CMDB table access for an integration connection
     */
    checkIntegrationConnectionCMDBAccessRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling checkIntegrationConnectionCMDBAccess().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling checkIntegrationConnectionCMDBAccess().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/connections/{connectionId}/cmdb/access-check`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
                body: IntegrationConnectionCMDBAccessCheckRequestToJSON(requestParameters['integrationConnectionCMDBAccessCheckRequest']),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => IntegrationConnectionCMDBAccessCheckResponseFromJSON(jsonValue));
        });
    }
    /**
     * Validates CMDB-related read access required for sync and, when policy-enabled, writeback-related table access required for CI create/update workflows.
     * Validate CMDB table access for an integration connection
     */
    checkIntegrationConnectionCMDBAccess(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.checkIntegrationConnectionCMDBAccessRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Creates an organization-scoped collaboration or ITSM integration connection.
     * Create an integration connection
     */
    createIntegrationConnectionRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling createIntegrationConnection().');
            }
            if (requestParameters['integrationConnectionCreate'] == null) {
                throw new runtime.RequiredError('integrationConnectionCreate', 'Required parameter "integrationConnectionCreate" was null or undefined when calling createIntegrationConnection().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/connections`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
                body: IntegrationConnectionCreateToJSON(requestParameters['integrationConnectionCreate']),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => IntegrationConnectionFromJSON(jsonValue));
        });
    }
    /**
     * Creates an organization-scoped collaboration or ITSM integration connection.
     * Create an integration connection
     */
    createIntegrationConnection(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.createIntegrationConnectionRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Alias one REAP network device to a canonical survivor without destructive merge
     * @deprecated
     */
    createIntegrationConnectionCMDBEntityAliasRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling createIntegrationConnectionCMDBEntityAlias().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling createIntegrationConnectionCMDBEntityAlias().');
            }
            if (requestParameters['cMDBEntityAliasCreateRequest'] == null) {
                throw new runtime.RequiredError('cMDBEntityAliasCreateRequest', 'Required parameter "cMDBEntityAliasCreateRequest" was null or undefined when calling createIntegrationConnectionCMDBEntityAlias().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/connections/{connectionId}/cmdb/entity-aliases`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
                body: CMDBEntityAliasCreateRequestToJSON(requestParameters['cMDBEntityAliasCreateRequest']),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CMDBEntityAliasFromJSON(jsonValue));
        });
    }
    /**
     * Alias one REAP network device to a canonical survivor without destructive merge
     * @deprecated
     */
    createIntegrationConnectionCMDBEntityAlias(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.createIntegrationConnectionCMDBEntityAliasRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Create a draft mapping profile
     * @deprecated
     */
    createIntegrationConnectionCMDBRelationMappingProfileRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling createIntegrationConnectionCMDBRelationMappingProfile().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling createIntegrationConnectionCMDBRelationMappingProfile().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/connections/{connectionId}/cmdb/relation-mapping-profile`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CMDBRelationMappingProfileFromJSON(jsonValue));
        });
    }
    /**
     * Create a draft mapping profile
     * @deprecated
     */
    createIntegrationConnectionCMDBRelationMappingProfile(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.createIntegrationConnectionCMDBRelationMappingProfileRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Create an interaction routing rule
     */
    createInteractionRoutingRuleRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling createInteractionRoutingRule().');
            }
            if (requestParameters['interactionRoutingRuleCreateRequest'] == null) {
                throw new runtime.RequiredError('interactionRoutingRuleCreateRequest', 'Required parameter "interactionRoutingRuleCreateRequest" was null or undefined when calling createInteractionRoutingRule().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/interaction-routing-rules`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
                body: InteractionRoutingRuleCreateRequestToJSON(requestParameters['interactionRoutingRuleCreateRequest']),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => InteractionRoutingRuleFromJSON(jsonValue));
        });
    }
    /**
     * Create an interaction routing rule
     */
    createInteractionRoutingRule(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.createInteractionRoutingRuleRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Create an interaction target
     */
    createInteractionTargetRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling createInteractionTarget().');
            }
            if (requestParameters['interactionTargetCreateRequest'] == null) {
                throw new runtime.RequiredError('interactionTargetCreateRequest', 'Required parameter "interactionTargetCreateRequest" was null or undefined when calling createInteractionTarget().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/interaction-targets`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
                body: InteractionTargetCreateRequestToJSON(requestParameters['interactionTargetCreateRequest']),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => InteractionTargetFromJSON(jsonValue));
        });
    }
    /**
     * Create an interaction target
     */
    createInteractionTarget(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.createInteractionTargetRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Delete an integration connection
     */
    deleteIntegrationConnectionRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling deleteIntegrationConnection().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling deleteIntegrationConnection().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/connections/{connectionId}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'DELETE',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.VoidApiResponse(response);
        });
    }
    /**
     * Delete an integration connection
     */
    deleteIntegrationConnection(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.deleteIntegrationConnectionRaw(requestParameters, initOverrides);
        });
    }
    /**
     * Delete an interaction routing rule
     */
    deleteInteractionRoutingRuleRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling deleteInteractionRoutingRule().');
            }
            if (requestParameters['ruleId'] == null) {
                throw new runtime.RequiredError('ruleId', 'Required parameter "ruleId" was null or undefined when calling deleteInteractionRoutingRule().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/interaction-routing-rules/{ruleId}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"ruleId"}}`, encodeURIComponent(String(requestParameters['ruleId']))),
                method: 'DELETE',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.VoidApiResponse(response);
        });
    }
    /**
     * Delete an interaction routing rule
     */
    deleteInteractionRoutingRule(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.deleteInteractionRoutingRuleRaw(requestParameters, initOverrides);
        });
    }
    /**
     * Delete an interaction target
     */
    deleteInteractionTargetRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling deleteInteractionTarget().');
            }
            if (requestParameters['targetId'] == null) {
                throw new runtime.RequiredError('targetId', 'Required parameter "targetId" was null or undefined when calling deleteInteractionTarget().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/interaction-targets/{targetId}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"targetId"}}`, encodeURIComponent(String(requestParameters['targetId']))),
                method: 'DELETE',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.VoidApiResponse(response);
        });
    }
    /**
     * Delete an interaction target
     */
    deleteInteractionTarget(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.deleteInteractionTargetRaw(requestParameters, initOverrides);
        });
    }
    /**
     * Returns the versioned REAP Teams app ZIP only when the connection belongs to the active organization and is a Teams connection. The caller gives this package to the tenant\'s Teams catalog administrator for upload before the app can be installed into a team.
     * Download the Teams app package for a connected tenant
     */
    downloadTeamsAppPackageRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling downloadTeamsAppPackage().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling downloadTeamsAppPackage().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/connections/{connectionId}/teams-app-package`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.BlobApiResponse(response);
        });
    }
    /**
     * Returns the versioned REAP Teams app ZIP only when the connection belongs to the active organization and is a Teams connection. The caller gives this package to the tenant\'s Teams catalog administrator for upload before the app can be installed into a team.
     * Download the Teams app package for a connected tenant
     */
    downloadTeamsAppPackage(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.downloadTeamsAppPackageRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Queue a CMDB synchronization on the integrations-owned worker
     */
    enqueueIntegrationConnectionCMDBSyncRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling enqueueIntegrationConnectionCMDBSync().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling enqueueIntegrationConnectionCMDBSync().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/connections/{connectionId}/cmdb/sync-requests`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
                body: IntegrationConnectionCMDBSyncRequestToJSON(requestParameters['integrationConnectionCMDBSyncRequest']),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => IntegrationConnectionCMDBSyncJobFromJSON(jsonValue));
        });
    }
    /**
     * Queue a CMDB synchronization on the integrations-owned worker
     */
    enqueueIntegrationConnectionCMDBSync(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.enqueueIntegrationConnectionCMDBSyncRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Executes a previously approved governed publication proposal through the integrations-owned provider registry. Only approved update-field proposals are supported in this phase.
     * Execute an approved governed publication proposal
     */
    executeIntegrationConnectionCMDBPublicationProposalRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling executeIntegrationConnectionCMDBPublicationProposal().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling executeIntegrationConnectionCMDBPublicationProposal().');
            }
            if (requestParameters['proposalId'] == null) {
                throw new runtime.RequiredError('proposalId', 'Required parameter "proposalId" was null or undefined when calling executeIntegrationConnectionCMDBPublicationProposal().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/connections/{connectionId}/cmdb/publication/proposals/{proposalId}/execute`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))).replace(`{${"proposalId"}}`, encodeURIComponent(String(requestParameters['proposalId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CMDBPublicationExecutionResultFromJSON(jsonValue));
        });
    }
    /**
     * Executes a previously approved governed publication proposal through the integrations-owned provider registry. Only approved update-field proposals are supported in this phase.
     * Execute an approved governed publication proposal
     */
    executeIntegrationConnectionCMDBPublicationProposal(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.executeIntegrationConnectionCMDBPublicationProposalRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Get an integration connection
     */
    getIntegrationConnectionRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling getIntegrationConnection().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling getIntegrationConnection().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/connections/{connectionId}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => IntegrationConnectionFromJSON(jsonValue));
        });
    }
    /**
     * Get an integration connection
     */
    getIntegrationConnection(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.getIntegrationConnectionRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Get reproducible mapping and decision history for a source identity
     */
    getIntegrationConnectionCMDBIdentityHistoryRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling getIntegrationConnectionCMDBIdentityHistory().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling getIntegrationConnectionCMDBIdentityHistory().');
            }
            if (requestParameters['sourceIdentityId'] == null) {
                throw new runtime.RequiredError('sourceIdentityId', 'Required parameter "sourceIdentityId" was null or undefined when calling getIntegrationConnectionCMDBIdentityHistory().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/connections/{connectionId}/cmdb/source-records/{sourceIdentityId}/identity-history`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))).replace(`{${"sourceIdentityId"}}`, encodeURIComponent(String(requestParameters['sourceIdentityId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CMDBIdentityHistoryFromJSON(jsonValue));
        });
    }
    /**
     * Get reproducible mapping and decision history for a source identity
     */
    getIntegrationConnectionCMDBIdentityHistory(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.getIntegrationConnectionCMDBIdentityHistoryRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Get the active provider-neutral identity policy
     * @deprecated
     */
    getIntegrationConnectionCMDBIdentityPolicyRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling getIntegrationConnectionCMDBIdentityPolicy().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling getIntegrationConnectionCMDBIdentityPolicy().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/connections/{connectionId}/cmdb/identity-policy`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CMDBIdentityPolicyFromJSON(jsonValue));
        });
    }
    /**
     * Get the active provider-neutral identity policy
     * @deprecated
     */
    getIntegrationConnectionCMDBIdentityPolicy(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.getIntegrationConnectionCMDBIdentityPolicyRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Get the active canonical relation mapping profile
     * @deprecated
     */
    getIntegrationConnectionCMDBRelationMappingProfileRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling getIntegrationConnectionCMDBRelationMappingProfile().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling getIntegrationConnectionCMDBRelationMappingProfile().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/connections/{connectionId}/cmdb/relation-mapping-profile`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CMDBRelationMappingProfileFromJSON(jsonValue));
        });
    }
    /**
     * Get the active canonical relation mapping profile
     * @deprecated
     */
    getIntegrationConnectionCMDBRelationMappingProfile(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.getIntegrationConnectionCMDBRelationMappingProfileRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Get immutable source-evidence diagnostics for a CMDB connection
     */
    getIntegrationConnectionCMDBSourceEvidenceRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling getIntegrationConnectionCMDBSourceEvidence().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling getIntegrationConnectionCMDBSourceEvidence().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/connections/{connectionId}/cmdb/source-evidence`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => IntegrationConnectionCMDBSourceEvidenceSummaryFromJSON(jsonValue));
        });
    }
    /**
     * Get immutable source-evidence diagnostics for a CMDB connection
     */
    getIntegrationConnectionCMDBSourceEvidence(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.getIntegrationConnectionCMDBSourceEvidenceRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Get immutable versions and occurrences for one source identity
     */
    getIntegrationConnectionCMDBSourceRecordHistoryRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling getIntegrationConnectionCMDBSourceRecordHistory().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling getIntegrationConnectionCMDBSourceRecordHistory().');
            }
            if (requestParameters['sourceIdentityId'] == null) {
                throw new runtime.RequiredError('sourceIdentityId', 'Required parameter "sourceIdentityId" was null or undefined when calling getIntegrationConnectionCMDBSourceRecordHistory().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/connections/{connectionId}/cmdb/source-records/{sourceIdentityId}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))).replace(`{${"sourceIdentityId"}}`, encodeURIComponent(String(requestParameters['sourceIdentityId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CMDBSourceRecordHistoryFromJSON(jsonValue));
        });
    }
    /**
     * Get immutable versions and occurrences for one source identity
     */
    getIntegrationConnectionCMDBSourceRecordHistory(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.getIntegrationConnectionCMDBSourceRecordHistoryRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Get the active source evidence retention policy
     */
    getIntegrationConnectionCMDBSourceRetentionPolicyRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling getIntegrationConnectionCMDBSourceRetentionPolicy().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling getIntegrationConnectionCMDBSourceRetentionPolicy().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/connections/{connectionId}/cmdb/source-retention-policy`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CMDBSourceRetentionPolicyFromJSON(jsonValue));
        });
    }
    /**
     * Get the active source evidence retention policy
     */
    getIntegrationConnectionCMDBSourceRetentionPolicy(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.getIntegrationConnectionCMDBSourceRetentionPolicyRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Get a queued CMDB synchronization status
     */
    getIntegrationConnectionCMDBSyncRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling getIntegrationConnectionCMDBSync().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling getIntegrationConnectionCMDBSync().');
            }
            if (requestParameters['syncRequestId'] == null) {
                throw new runtime.RequiredError('syncRequestId', 'Required parameter "syncRequestId" was null or undefined when calling getIntegrationConnectionCMDBSync().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/connections/{connectionId}/cmdb/sync-requests/{syncRequestId}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))).replace(`{${"syncRequestId"}}`, encodeURIComponent(String(requestParameters['syncRequestId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => IntegrationConnectionCMDBSyncJobFromJSON(jsonValue));
        });
    }
    /**
     * Get a queued CMDB synchronization status
     */
    getIntegrationConnectionCMDBSync(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.getIntegrationConnectionCMDBSyncRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Returns the persisted CMDB writeback policy for the specified integration connection.
     * Get CMDB writeback policy for an integration connection
     */
    getIntegrationConnectionCMDBWritebackPolicyRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling getIntegrationConnectionCMDBWritebackPolicy().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling getIntegrationConnectionCMDBWritebackPolicy().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/connections/{connectionId}/cmdb/writeback-policy`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => IntegrationConnectionCMDBWritebackPolicyFromJSON(jsonValue));
        });
    }
    /**
     * Returns the persisted CMDB writeback policy for the specified integration connection.
     * Get CMDB writeback policy for an integration connection
     */
    getIntegrationConnectionCMDBWritebackPolicy(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.getIntegrationConnectionCMDBWritebackPolicyRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Get integration webhook provisioning details
     */
    getIntegrationConnectionWebhookProvisioningRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling getIntegrationConnectionWebhookProvisioning().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling getIntegrationConnectionWebhookProvisioning().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/connections/{connectionId}/webhook-provisioning`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => IntegrationWebhookProvisioningFromJSON(jsonValue));
        });
    }
    /**
     * Get integration webhook provisioning details
     */
    getIntegrationConnectionWebhookProvisioning(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.getIntegrationConnectionWebhookProvisioningRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Get an interaction routing rule
     */
    getInteractionRoutingRuleRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling getInteractionRoutingRule().');
            }
            if (requestParameters['ruleId'] == null) {
                throw new runtime.RequiredError('ruleId', 'Required parameter "ruleId" was null or undefined when calling getInteractionRoutingRule().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/interaction-routing-rules/{ruleId}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"ruleId"}}`, encodeURIComponent(String(requestParameters['ruleId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => InteractionRoutingRuleFromJSON(jsonValue));
        });
    }
    /**
     * Get an interaction routing rule
     */
    getInteractionRoutingRule(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.getInteractionRoutingRuleRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Get an interaction target
     */
    getInteractionTargetRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling getInteractionTarget().');
            }
            if (requestParameters['targetId'] == null) {
                throw new runtime.RequiredError('targetId', 'Required parameter "targetId" was null or undefined when calling getInteractionTarget().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/interaction-targets/{targetId}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"targetId"}}`, encodeURIComponent(String(requestParameters['targetId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => InteractionTargetFromJSON(jsonValue));
        });
    }
    /**
     * Get an interaction target
     */
    getInteractionTarget(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.getInteractionTargetRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * List reversible network-device aliases
     * @deprecated
     */
    listIntegrationConnectionCMDBEntityAliasesRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling listIntegrationConnectionCMDBEntityAliases().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling listIntegrationConnectionCMDBEntityAliases().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/connections/{connectionId}/cmdb/entity-aliases`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CMDBEntityAliasesResponseFromJSON(jsonValue));
        });
    }
    /**
     * List reversible network-device aliases
     * @deprecated
     */
    listIntegrationConnectionCMDBEntityAliases(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.listIntegrationConnectionCMDBEntityAliasesRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * List identity findings requiring review
     */
    listIntegrationConnectionCMDBIdentityFindingsRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling listIntegrationConnectionCMDBIdentityFindings().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling listIntegrationConnectionCMDBIdentityFindings().');
            }
            const queryParameters = {};
            if (requestParameters['status'] != null) {
                queryParameters['status'] = requestParameters['status'];
            }
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/connections/{connectionId}/cmdb/identity-findings`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CMDBIdentityFindingsResponseFromJSON(jsonValue));
        });
    }
    /**
     * List identity findings requiring review
     */
    listIntegrationConnectionCMDBIdentityFindings(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.listIntegrationConnectionCMDBIdentityFindingsRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * List connection-isolated immutable CMDB source identities
     */
    listIntegrationConnectionCMDBSourceRecordsRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling listIntegrationConnectionCMDBSourceRecords().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling listIntegrationConnectionCMDBSourceRecords().');
            }
            const queryParameters = {};
            if (requestParameters['domain'] != null) {
                queryParameters['domain'] = requestParameters['domain'];
            }
            if (requestParameters['limit'] != null) {
                queryParameters['limit'] = requestParameters['limit'];
            }
            if (requestParameters['offset'] != null) {
                queryParameters['offset'] = requestParameters['offset'];
            }
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/connections/{connectionId}/cmdb/source-records`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CMDBSourceRecordsResponseFromJSON(jsonValue));
        });
    }
    /**
     * List connection-isolated immutable CMDB source identities
     */
    listIntegrationConnectionCMDBSourceRecords(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.listIntegrationConnectionCMDBSourceRecordsRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Returns the configured integration connections for the active REAP organization.
     * List integration connections
     */
    listIntegrationConnectionsRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling listIntegrationConnections().');
            }
            const queryParameters = {};
            if (requestParameters['q'] != null) {
                queryParameters['q'] = requestParameters['q'];
            }
            if (requestParameters['provider'] != null) {
                queryParameters['provider'] = requestParameters['provider'];
            }
            if (requestParameters['status'] != null) {
                queryParameters['status'] = requestParameters['status'];
            }
            if (requestParameters['limit'] != null) {
                queryParameters['limit'] = requestParameters['limit'];
            }
            if (requestParameters['offset'] != null) {
                queryParameters['offset'] = requestParameters['offset'];
            }
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/connections`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => IntegrationConnectionsListResponseFromJSON(jsonValue));
        });
    }
    /**
     * Returns the configured integration connections for the active REAP organization.
     * List integration connections
     */
    listIntegrationConnections(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.listIntegrationConnectionsRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * List interaction routing rules
     */
    listInteractionRoutingRulesRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling listInteractionRoutingRules().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/interaction-routing-rules`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => InteractionRoutingRulesListResponseFromJSON(jsonValue));
        });
    }
    /**
     * List interaction routing rules
     */
    listInteractionRoutingRules(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.listInteractionRoutingRulesRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * List interaction targets
     */
    listInteractionTargetsRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling listInteractionTargets().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/interaction-targets`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => InteractionTargetsListResponseFromJSON(jsonValue));
        });
    }
    /**
     * List interaction targets
     */
    listInteractionTargets(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.listInteractionTargetsRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Returns the configured provider catalog for the active REAP organization, including provider fields and any runtime bootstrap metadata required by the REAP UI.
     * List available integration provider definitions
     */
    orgsOrgIdIntegrationsProviderDefinitionsGetRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdIntegrationsProviderDefinitionsGet().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/provider-definitions`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => ProviderDefinitionsListResponseFromJSON(jsonValue));
        });
    }
    /**
     * Returns the configured provider catalog for the active REAP organization, including provider fields and any runtime bootstrap metadata required by the REAP UI.
     * List available integration provider definitions
     */
    orgsOrgIdIntegrationsProviderDefinitionsGet(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdIntegrationsProviderDefinitionsGetRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Perform the next integration setup action
     */
    orgsOrgIdIntegrationsSetupProviderActionsPostRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdIntegrationsSetupProviderActionsPost().');
            }
            if (requestParameters['provider'] == null) {
                throw new runtime.RequiredError('provider', 'Required parameter "provider" was null or undefined when calling orgsOrgIdIntegrationsSetupProviderActionsPost().');
            }
            if (requestParameters['orgsOrgIdIntegrationsSetupProviderActionsPostRequest'] == null) {
                throw new runtime.RequiredError('orgsOrgIdIntegrationsSetupProviderActionsPostRequest', 'Required parameter "orgsOrgIdIntegrationsSetupProviderActionsPostRequest" was null or undefined when calling orgsOrgIdIntegrationsSetupProviderActionsPost().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/setup/{provider}/actions`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"provider"}}`, encodeURIComponent(String(requestParameters['provider']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
                body: OrgsOrgIdIntegrationsSetupProviderActionsPostRequestToJSON(requestParameters['orgsOrgIdIntegrationsSetupProviderActionsPostRequest']),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => OAuthStartResponseFromJSON(jsonValue));
        });
    }
    /**
     * Perform the next integration setup action
     */
    orgsOrgIdIntegrationsSetupProviderActionsPost(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdIntegrationsSetupProviderActionsPostRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Idempotently removes the active pending setup request for this organization and provider.
     * Cancel the current integration setup request
     */
    orgsOrgIdIntegrationsSetupProviderDeleteRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdIntegrationsSetupProviderDelete().');
            }
            if (requestParameters['provider'] == null) {
                throw new runtime.RequiredError('provider', 'Required parameter "provider" was null or undefined when calling orgsOrgIdIntegrationsSetupProviderDelete().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/setup/{provider}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"provider"}}`, encodeURIComponent(String(requestParameters['provider']))),
                method: 'DELETE',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.VoidApiResponse(response);
        });
    }
    /**
     * Idempotently removes the active pending setup request for this organization and provider.
     * Cancel the current integration setup request
     */
    orgsOrgIdIntegrationsSetupProviderDelete(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.orgsOrgIdIntegrationsSetupProviderDeleteRaw(requestParameters, initOverrides);
        });
    }
    /**
     * Get the current integration setup request
     */
    orgsOrgIdIntegrationsSetupProviderGetRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdIntegrationsSetupProviderGet().');
            }
            if (requestParameters['provider'] == null) {
                throw new runtime.RequiredError('provider', 'Required parameter "provider" was null or undefined when calling orgsOrgIdIntegrationsSetupProviderGet().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/setup/{provider}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"provider"}}`, encodeURIComponent(String(requestParameters['provider']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => IntegrationSetupRequestFromJSON(jsonValue));
        });
    }
    /**
     * Get the current integration setup request
     */
    orgsOrgIdIntegrationsSetupProviderGet(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdIntegrationsSetupProviderGetRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Completes per-user Slack account linking for a workspace-installed REAP Slack app. The request must include a signed link token minted from a trusted Slack-owned flow, and the authenticated caller must already have access to the target REAP organization.
     * Complete Slack account linking for the authenticated REAP user
     */
    orgsOrgIdIntegrationsSlackLinkCompletePostRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdIntegrationsSlackLinkCompletePost().');
            }
            if (requestParameters['slackLinkCompleteRequest'] == null) {
                throw new runtime.RequiredError('slackLinkCompleteRequest', 'Required parameter "slackLinkCompleteRequest" was null or undefined when calling orgsOrgIdIntegrationsSlackLinkCompletePost().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/slack/link/complete`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
                body: SlackLinkCompleteRequestToJSON(requestParameters['slackLinkCompleteRequest']),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => SlackLinkCompleteResponseFromJSON(jsonValue));
        });
    }
    /**
     * Completes per-user Slack account linking for a workspace-installed REAP Slack app. The request must include a signed link token minted from a trusted Slack-owned flow, and the authenticated caller must already have access to the target REAP organization.
     * Complete Slack account linking for the authenticated REAP user
     */
    orgsOrgIdIntegrationsSlackLinkCompletePost(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdIntegrationsSlackLinkCompletePostRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Browser callback used by Slack after the installer approves the REAP app. REAP validates the signed cookie state, exchanges Slack\'s temporary code server-to-server, creates the Slack integration connection, and redirects back to REAP.
     * Complete Slack OAuth callback
     */
    publicSlackOauthCallbackGetRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const queryParameters = {};
            if (requestParameters['code'] != null) {
                queryParameters['code'] = requestParameters['code'];
            }
            if (requestParameters['state'] != null) {
                queryParameters['state'] = requestParameters['state'];
            }
            if (requestParameters['error'] != null) {
                queryParameters['error'] = requestParameters['error'];
            }
            const headerParameters = {};
            const response = yield this.request({
                path: `/public/slack/oauth/callback`,
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.VoidApiResponse(response);
        });
    }
    /**
     * Browser callback used by Slack after the installer approves the REAP app. REAP validates the signed cookie state, exchanges Slack\'s temporary code server-to-server, creates the Slack integration connection, and redirects back to REAP.
     * Complete Slack OAuth callback
     */
    publicSlackOauthCallbackGet() {
        return __awaiter(this, arguments, void 0, function* (requestParameters = {}, initOverrides) {
            yield this.publicSlackOauthCallbackGetRaw(requestParameters, initOverrides);
        });
    }
    /**
     * Replace rules in a draft mapping profile
     * @deprecated
     */
    replaceIntegrationConnectionCMDBRelationMappingRulesRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling replaceIntegrationConnectionCMDBRelationMappingRules().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling replaceIntegrationConnectionCMDBRelationMappingRules().');
            }
            if (requestParameters['profileId'] == null) {
                throw new runtime.RequiredError('profileId', 'Required parameter "profileId" was null or undefined when calling replaceIntegrationConnectionCMDBRelationMappingRules().');
            }
            if (requestParameters['cMDBCanonicalRelationMappingRulesRequest'] == null) {
                throw new runtime.RequiredError('cMDBCanonicalRelationMappingRulesRequest', 'Required parameter "cMDBCanonicalRelationMappingRulesRequest" was null or undefined when calling replaceIntegrationConnectionCMDBRelationMappingRules().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/connections/{connectionId}/cmdb/relation-mapping-profiles/{profileId}/rules`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))).replace(`{${"profileId"}}`, encodeURIComponent(String(requestParameters['profileId']))),
                method: 'PUT',
                headers: headerParameters,
                query: queryParameters,
                body: CMDBCanonicalRelationMappingRulesRequestToJSON(requestParameters['cMDBCanonicalRelationMappingRulesRequest']),
            }, initOverrides);
            return new runtime.VoidApiResponse(response);
        });
    }
    /**
     * Replace rules in a draft mapping profile
     * @deprecated
     */
    replaceIntegrationConnectionCMDBRelationMappingRules(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.replaceIntegrationConnectionCMDBRelationMappingRulesRaw(requestParameters, initOverrides);
        });
    }
    /**
     * Correct a previous alias while preserving its audit history
     * @deprecated
     */
    splitIntegrationConnectionCMDBEntityAliasRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling splitIntegrationConnectionCMDBEntityAlias().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling splitIntegrationConnectionCMDBEntityAlias().');
            }
            if (requestParameters['aliasId'] == null) {
                throw new runtime.RequiredError('aliasId', 'Required parameter "aliasId" was null or undefined when calling splitIntegrationConnectionCMDBEntityAlias().');
            }
            if (requestParameters['cMDBEntityAliasSplitRequest'] == null) {
                throw new runtime.RequiredError('cMDBEntityAliasSplitRequest', 'Required parameter "cMDBEntityAliasSplitRequest" was null or undefined when calling splitIntegrationConnectionCMDBEntityAlias().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/connections/{connectionId}/cmdb/entity-aliases/{aliasId}/split`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))).replace(`{${"aliasId"}}`, encodeURIComponent(String(requestParameters['aliasId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
                body: CMDBEntityAliasSplitRequestToJSON(requestParameters['cMDBEntityAliasSplitRequest']),
            }, initOverrides);
            return new runtime.VoidApiResponse(response);
        });
    }
    /**
     * Correct a previous alias while preserving its audit history
     * @deprecated
     */
    splitIntegrationConnectionCMDBEntityAlias(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.splitIntegrationConnectionCMDBEntityAliasRaw(requestParameters, initOverrides);
        });
    }
    /**
     * Starts a REAP-initiated collaboration OAuth or tenant-consent flow for the authenticated user\'s active organization. REAP stores the organization binding in a short-lived secure browser cookie and returns the provider authorization URL.
     * Start collaboration OAuth for the active REAP organization
     */
    startIntegrationOAuthRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling startIntegrationOAuth().');
            }
            if (requestParameters['oAuthStartRequest'] == null) {
                throw new runtime.RequiredError('oAuthStartRequest', 'Required parameter "oAuthStartRequest" was null or undefined when calling startIntegrationOAuth().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/oauth/start`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
                body: OAuthStartRequestToJSON(requestParameters['oAuthStartRequest']),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => OAuthStartResponseFromJSON(jsonValue));
        });
    }
    /**
     * Starts a REAP-initiated collaboration OAuth or tenant-consent flow for the authenticated user\'s active organization. REAP stores the organization binding in a short-lived secure browser cookie and returns the provider authorization URL.
     * Start collaboration OAuth for the active REAP organization
     */
    startIntegrationOAuth(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.startIntegrationOAuthRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Starts a REAP-initiated Slack installation flow for the authenticated user\'s active organization. REAP stores the organization binding in a short-lived secure browser cookie and returns the Slack authorization URL.
     * Start Slack OAuth for the active REAP organization
     */
    startSlackOAuthRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling startSlackOAuth().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/slack/oauth/start`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => OAuthStartResponseFromJSON(jsonValue));
        });
    }
    /**
     * Starts a REAP-initiated Slack installation flow for the authenticated user\'s active organization. REAP stores the organization binding in a short-lived secure browser cookie and returns the Slack authorization URL.
     * Start Slack OAuth for the active REAP organization
     */
    startSlackOAuth(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.startSlackOAuthRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Starts a REAP-initiated Microsoft Entra authorization-code flow for the authenticated user\'s active organization. If the signed-in member cannot grant the requested delegated Microsoft Graph permission, Entra\'s tenant-configured admin-consent workflow handles the approval request. After approval, the REAP administrator starts this flow again to complete the connection. REAP stores the organization binding in a short-lived secure browser cookie.
     * Start Teams Entra authorization for the active REAP organization
     */
    startTeamsOAuthRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling startTeamsOAuth().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/teams/oauth/start`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => OAuthStartResponseFromJSON(jsonValue));
        });
    }
    /**
     * Starts a REAP-initiated Microsoft Entra authorization-code flow for the authenticated user\'s active organization. If the signed-in member cannot grant the requested delegated Microsoft Graph permission, Entra\'s tenant-configured admin-consent workflow handles the approval request. After approval, the REAP administrator starts this flow again to complete the connection. REAP stores the organization binding in a short-lived secure browser cookie.
     * Start Teams Entra authorization for the active REAP organization
     */
    startTeamsOAuth(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.startTeamsOAuthRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Test an integration connection
     */
    testIntegrationConnectionRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling testIntegrationConnection().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling testIntegrationConnection().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/connections/{connectionId}/test`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => IntegrationConnectionTestResultFromJSON(jsonValue));
        });
    }
    /**
     * Test an integration connection
     */
    testIntegrationConnection(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.testIntegrationConnectionRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Update an integration connection
     */
    updateIntegrationConnectionRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling updateIntegrationConnection().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling updateIntegrationConnection().');
            }
            if (requestParameters['integrationConnectionUpdate'] == null) {
                throw new runtime.RequiredError('integrationConnectionUpdate', 'Required parameter "integrationConnectionUpdate" was null or undefined when calling updateIntegrationConnection().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/connections/{connectionId}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'PATCH',
                headers: headerParameters,
                query: queryParameters,
                body: IntegrationConnectionUpdateToJSON(requestParameters['integrationConnectionUpdate']),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => IntegrationConnectionFromJSON(jsonValue));
        });
    }
    /**
     * Update an integration connection
     */
    updateIntegrationConnection(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.updateIntegrationConnectionRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Create and activate a new identity-policy version
     * @deprecated
     */
    updateIntegrationConnectionCMDBIdentityPolicyRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling updateIntegrationConnectionCMDBIdentityPolicy().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling updateIntegrationConnectionCMDBIdentityPolicy().');
            }
            if (requestParameters['cMDBIdentityPolicyUpdateRequest'] == null) {
                throw new runtime.RequiredError('cMDBIdentityPolicyUpdateRequest', 'Required parameter "cMDBIdentityPolicyUpdateRequest" was null or undefined when calling updateIntegrationConnectionCMDBIdentityPolicy().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/connections/{connectionId}/cmdb/identity-policy`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'PUT',
                headers: headerParameters,
                query: queryParameters,
                body: CMDBIdentityPolicyUpdateRequestToJSON(requestParameters['cMDBIdentityPolicyUpdateRequest']),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CMDBIdentityPolicyFromJSON(jsonValue));
        });
    }
    /**
     * Create and activate a new identity-policy version
     * @deprecated
     */
    updateIntegrationConnectionCMDBIdentityPolicy(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.updateIntegrationConnectionCMDBIdentityPolicyRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Create and activate a source evidence retention policy version
     */
    updateIntegrationConnectionCMDBSourceRetentionPolicyRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling updateIntegrationConnectionCMDBSourceRetentionPolicy().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling updateIntegrationConnectionCMDBSourceRetentionPolicy().');
            }
            if (requestParameters['cMDBSourceRetentionPolicyUpdateRequest'] == null) {
                throw new runtime.RequiredError('cMDBSourceRetentionPolicyUpdateRequest', 'Required parameter "cMDBSourceRetentionPolicyUpdateRequest" was null or undefined when calling updateIntegrationConnectionCMDBSourceRetentionPolicy().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/connections/{connectionId}/cmdb/source-retention-policy`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'PUT',
                headers: headerParameters,
                query: queryParameters,
                body: CMDBSourceRetentionPolicyUpdateRequestToJSON(requestParameters['cMDBSourceRetentionPolicyUpdateRequest']),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CMDBSourceRetentionPolicyFromJSON(jsonValue));
        });
    }
    /**
     * Create and activate a source evidence retention policy version
     */
    updateIntegrationConnectionCMDBSourceRetentionPolicy(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.updateIntegrationConnectionCMDBSourceRetentionPolicyRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Persists the CMDB writeback policy used by later REAP-to-CMDB CI writeback actions.
     * Update CMDB writeback policy for an integration connection
     */
    updateIntegrationConnectionCMDBWritebackPolicyRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling updateIntegrationConnectionCMDBWritebackPolicy().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling updateIntegrationConnectionCMDBWritebackPolicy().');
            }
            if (requestParameters['integrationConnectionCMDBWritebackPolicyUpdateRequest'] == null) {
                throw new runtime.RequiredError('integrationConnectionCMDBWritebackPolicyUpdateRequest', 'Required parameter "integrationConnectionCMDBWritebackPolicyUpdateRequest" was null or undefined when calling updateIntegrationConnectionCMDBWritebackPolicy().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/connections/{connectionId}/cmdb/writeback-policy`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'PUT',
                headers: headerParameters,
                query: queryParameters,
                body: IntegrationConnectionCMDBWritebackPolicyUpdateRequestToJSON(requestParameters['integrationConnectionCMDBWritebackPolicyUpdateRequest']),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => IntegrationConnectionCMDBWritebackPolicyFromJSON(jsonValue));
        });
    }
    /**
     * Persists the CMDB writeback policy used by later REAP-to-CMDB CI writeback actions.
     * Update CMDB writeback policy for an integration connection
     */
    updateIntegrationConnectionCMDBWritebackPolicy(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.updateIntegrationConnectionCMDBWritebackPolicyRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Update an interaction routing rule
     */
    updateInteractionRoutingRuleRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling updateInteractionRoutingRule().');
            }
            if (requestParameters['ruleId'] == null) {
                throw new runtime.RequiredError('ruleId', 'Required parameter "ruleId" was null or undefined when calling updateInteractionRoutingRule().');
            }
            if (requestParameters['body'] == null) {
                throw new runtime.RequiredError('body', 'Required parameter "body" was null or undefined when calling updateInteractionRoutingRule().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/interaction-routing-rules/{ruleId}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"ruleId"}}`, encodeURIComponent(String(requestParameters['ruleId']))),
                method: 'PATCH',
                headers: headerParameters,
                query: queryParameters,
                body: requestParameters['body'],
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => InteractionRoutingRuleFromJSON(jsonValue));
        });
    }
    /**
     * Update an interaction routing rule
     */
    updateInteractionRoutingRule(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.updateInteractionRoutingRuleRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Update an interaction target
     */
    updateInteractionTargetRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling updateInteractionTarget().');
            }
            if (requestParameters['targetId'] == null) {
                throw new runtime.RequiredError('targetId', 'Required parameter "targetId" was null or undefined when calling updateInteractionTarget().');
            }
            if (requestParameters['body'] == null) {
                throw new runtime.RequiredError('body', 'Required parameter "body" was null or undefined when calling updateInteractionTarget().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/interaction-targets/{targetId}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"targetId"}}`, encodeURIComponent(String(requestParameters['targetId']))),
                method: 'PATCH',
                headers: headerParameters,
                query: queryParameters,
                body: requestParameters['body'],
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => InteractionTargetFromJSON(jsonValue));
        });
    }
    /**
     * Update an interaction target
     */
    updateInteractionTarget(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.updateInteractionTargetRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Executes only operations permitted by the server-side Phase 0A publication controls. The operation ID is required for idempotent retry handling.
     * Execute an authorized, idempotent CMDB publication
     */
    writebackIntegrationConnectionCMDBRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling writebackIntegrationConnectionCMDB().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling writebackIntegrationConnectionCMDB().');
            }
            if (requestParameters['integrationConnectionCMDBPublicationRequest'] == null) {
                throw new runtime.RequiredError('integrationConnectionCMDBPublicationRequest', 'Required parameter "integrationConnectionCMDBPublicationRequest" was null or undefined when calling writebackIntegrationConnectionCMDB().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/connections/{connectionId}/cmdb/writeback`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
                body: IntegrationConnectionCMDBPublicationRequestToJSON(requestParameters['integrationConnectionCMDBPublicationRequest']),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => IntegrationConnectionCMDBWritebackResponseFromJSON(jsonValue));
        });
    }
    /**
     * Executes only operations permitted by the server-side Phase 0A publication controls. The operation ID is required for idempotent retry handling.
     * Execute an authorized, idempotent CMDB publication
     */
    writebackIntegrationConnectionCMDB(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.writebackIntegrationConnectionCMDBRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
}
/**
 * @export
 */
export const ListIntegrationConnectionCMDBIdentityFindingsStatusEnum = {
    Open: 'open',
    Assigned: 'assigned',
    Deferred: 'deferred',
    Resolved: 'resolved'
};
